import { React, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import API_BASE_URL from '../../../../Constants';

const RouteSummaryFields = [
    {key: 'routeID', value: 'Route ID'},
    {key: 'routeDescription', value: 'Route Description'},
    {key: 'employeeID', value: 'Employee ID'},
    {key: 'firstName', value: 'First Name'},
    {key: 'lastName', value: 'Last Name'},
    {key: 'territory', value: 'Territory'},
    {key: 'sites', value: 'Sites'},
    {key: 'devices', value: 'Devices'},
    {key: 'toDo', value: 'To Do'},
    {key: 'monthly', value: 'Monthly $'},
];

const OrderByFields = [
    {key: 'routeID', value: 'Route ID'},
    {key: 'employeeID', value: 'Employee ID'},
    {key: 'firstName', value: 'First Name'},
    {key: 'lastName', value: 'Last Name'},
];

const RouteSummary = () => {
    const [routes, setRoutes] = useState([]);
    const [viewMode, setViewMode] = useState('table');
    const [orderBy, setOrderBy] = useState('routeID');

    // Load API and fetch table items
    useEffect(() => {
        const fetchRouteSummary = async () => {
            try {
                const response = await fetch(API_BASE_URL + '/api/maintenance/routesummary', {credentials: 'include'});
                if (!response.ok) {
                    throw new Error("Network Response not ok");
                }
                const data = await response.json();
                setRoutes(data);
            } catch(error) {
                console.error("Network Error: ", error);
            }
        }

        fetchRouteSummary()
        const intervalId = setInterval(fetchRouteSummary, 5000);

        return () => clearInterval(intervalId);
    }, []);

    //Sort Route Summary by selected attribute
    const sortRoutes = () => {
        if (['routeID', 'employeeID', 'firstName', 'lastName'].includes(orderBy)) {
            return routes.sort((r1, r2) => r1[orderBy] > r2[orderBy] ? 1 : -1);
        }
    }

    //Export to .xlsx file and download functionality
    const exportData = (data, filename) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename);
    }

    //Sum up the values in sites column
    const sumColumn = (columnName) => {
        if (['sites', 'devices', 'toDo', 'monthly'].includes(columnName)) {
            return routes.reduce((acc, route) => acc + route[columnName], 0);
        } else if (columnName === 'territory') {
            return `$${(sumColumn("monthly") / sumColumn("devices")).toFixed(2)} Average Per Device`;
        } else {
            return;
        }
    }

    return (
        <div className="mx-auto ml-4 h-screen w-full">
            <div className="flex justify-between items-center mb-4">
                <h2 className="mr-10 text-2xl font-bold">Route Summary</h2>
                <label className="flex items-center">
                    <span className="mr-2">Sort List By:</span>
                    <select 
                        value={orderBy} 
                        onChange={(e) => setOrderBy(e.target.value)} 
                        className="px-4 mr-10 py-1 border rounded-md"
                    >
                        {OrderByFields.map((field) => (
                            <option value={field.key}>{field.value}</option>
                        ))}
                    </select>
                </label>
                <label className="flex items-center">
                    <span className="mr-2">View Mode:</span>
                    <select value={viewMode} onChange={(e) => setViewMode(e.target.value)} className="px-4 mr-10 py-1 border rounded-md">
                        <option value="table">Table</option>
                        <option value="card">Card</option>
                    </select>
                </label>
                <label className="flex items-center">
                    <button
                        type="export"
                        className="py-2 px-4 mr-10 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        onClick = {() => exportData(sortRoutes(), 'RouteSummary.xlsx')}
                    >
                        Export Excel
                    </button>
                </label>
            </div>
            {viewMode === 'table' ? (
                <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200">
                    <thead className="bg-gray-200">
                        <tr>
                            {RouteSummaryFields.map((field) => (
                                <th key={field.key} className="px-4 py-2 text-left font-semibold text-gray-600">{field.value}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="text-gray-700">
                        {sortRoutes().map((route) => (
                            <tr key={route.routeID} className="border-t">
                                {RouteSummaryFields.map((field) => (
                                    <td key={field.key} className="px-4 py-2">
                                        {route[field.key] !== undefined ? route[field.key] : 'N/A'}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        <tr className="border-t">
                            {RouteSummaryFields.map((field) => (
                                field.key === 'routeID' ? (
                                    <td className="px-4 py-2 font-semibold">Total</td>
                                ) : (
                                    <td key={field.key} className="px-4 py-2">{sumColumn(field.key)}</td>
                                )
                            ))}
                        </tr>
                    </tbody>
                </table>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sortRoutes().map((route) => (
                        <div key={route.routeID} className="bg-white p-4 rounded-lg shadow-lg border-2 border-gray-200 flex items-center">
                            <img 
                                src="/favicon.ico" 
                                alt="Profile" 
                                className="w-16 h-16 rounded-full mr-4"
                            />
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold">{route.routeDescription}</h3>
                                <p className="text-sm text-gray-600">Route ID: {route.routeID}</p>
                                <p className="text-sm text-gray-600">Employee: {route.firstName} {route.lastName}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default RouteSummary;